import {
    endOfDay, endOfMonth, endOfQuarter, endOfWeek, format,
    getDate, getDaysInMonth, getMonth, getYear, isWithinInterval, parseISO, startOfDay,
    startOfMonth, startOfQuarter, startOfWeek, startOfYear, subMonths
} from 'date-fns';
import { es } from 'date-fns/locale';

import {
    AVERAGE_TICKET, clientDataRows, MexStatesDefault, months,
    salesDefault, stateRows
} from '../../../constants/constants';
import {
    FIRST_SEMESTER_MONTHS, NEW_CLIENTS_DATASET,
    SECOND_SEMESTER_MONTHS, TOP_PRODUCTS_DATA
} from '../../../constants/admin/dashboard_constants';
import { formatRange, getCurrentFortnight } from '../../dateHelper';
import { getTitle } from '../productHelper';
import { setData } from './topProducts/topProductsHelper';

const clone = value => JSON.parse(JSON.stringify(value));
const parseOrderDate = value => value instanceof Date ? value : parseISO(value);
const getOrderDate = order => order.reporting_date || order.order_date;
const parseOrder = order => ({
    ...order,
    parsedDate: order.parsedDate instanceof Date
        ? order.parsedDate
        : parseOrderDate(getOrderDate(order))
});

export const createDashboardInitialModel = today => ({
    allOrders: {},
    averageTicket: [],
    year: '',
    customers: [],
    topProductsData: clone(TOP_PRODUCTS_DATA),
    dateRanges: {
        today: format(today, 'd/MMMM/yyyy', { locale: es }),
        week: '',
        fortnight: '',
        month: '',
        quarter: '',
        yearToDate: '',
        annual: '',
        filtered: ''
    },
    todayTotalData: clone(salesDefault),
    weekTotalData: clone(salesDefault),
    monthTotalData: clone(salesDefault),
    quarterTotalData: clone(salesDefault),
    yearToDateTotalData: clone(salesDefault),
    filteredTotalData: clone(salesDefault),
    firstSemester: {
        labels: FIRST_SEMESTER_MONTHS,
        datasets: clone(NEW_CLIENTS_DATASET)
    },
    secondSemester: {
        labels: SECOND_SEMESTER_MONTHS,
        datasets: clone(NEW_CLIENTS_DATASET)
    },
    totalNewClients: 0,
    countrySalesData: clone(MexStatesDefault),
    topStatesData: clone(stateRows),
    fqtMonthlyData: clone(clientDataRows),
    fqtQuarterlyData: clone(clientDataRows),
    fqtYearToDateData: clone(clientDataRows),
    currentAverageTicket: clone(AVERAGE_TICKET)
});

const getTotal = orders => ({
    total: Math.round(
        orders.reduce((sum, order) => sum + order.summary.total, 0) * 1e2
    ) / 1e2,
    qty: orders.length
});

const getFirstPurchaseTotal = customer => Number(
    customer.firstPurchaseTotal ?? customer.firstPurchaseAmount ?? 0
);

const getDailyLabels = (year, month) => Array.from(
    { length: getDaysInMonth(new Date(year, month, 1)) },
    (_, index) => String(index + 1)
);

export const getMondayFirstMonthOffset = (year, month) =>
    (new Date(year, month, 1).getDay() + 6) % 7;

export const buildDailyNewCustomerMetrics = (customers, year, month) => {
    const labels = getDailyLabels(year, month);
    const values = new Array(labels.length).fill(0);

    customers.forEach((customer) => {
        if (customer.year !== year || customer.month !== month + 1)
            return;

        const day = Number(customer.day);
        if (day >= 1 && day <= values.length)
            values[day - 1] += 1;
    });

    return {
        labels,
        values,
        total: values.reduce((sum, value) => sum + value, 0),
        rows: values.map((count, index) => ({
            id: index + 1,
            day: index + 1,
            date: `${index + 1}/${months[month]}/${year}`,
            count
        }))
    };
};

export const buildDailyAverageTicketMetrics = (
    orders, customers, year, month, segment = 'general'
) => {
    const labels = getDailyLabels(year, month);
    const stats = labels.map(() => ({ total: 0, count: 0 }));

    if (segment === 'newCustomers') {
        customers.forEach((customer) => {
            if (customer.year !== year || customer.month !== month + 1)
                return;

            const day = Number(customer.day);
            if (day < 1 || day > stats.length)
                return;

            stats[day - 1].total += getFirstPurchaseTotal(customer);
            stats[day - 1].count += 1;
        });
    } else {
        orders.forEach((order) => {
            const parsedDate = parseOrder(order).parsedDate;
            if (getYear(parsedDate) !== year || getMonth(parsedDate) !== month)
                return;

            const day = getDate(parsedDate);
            stats[day - 1].total += Number(order.summary.total) || 0;
            stats[day - 1].count += 1;
        });
    }

    const values = stats.map(({ total, count }) => count > 0
        ? Math.round((total / count) * 100) / 100
        : 0
    );

    return {
        labels,
        values,
        totalOperations: stats.reduce((sum, value) => sum + value.count, 0),
        rows: stats.map(({ count }, index) => ({
            id: index + 1,
            day: index + 1,
            date: `${index + 1}/${months[month]}/${year}`,
            count,
            average: values[index],
            formattedAverage: `$${values[index].toFixed(2)}`
        }))
    };
};

export const orderTopProducts = (orders) => {
    const products = new Map();

    orders.forEach((order) => {
        order.products.forEach((product) => {
            const key = String(product.prod_var_id);
            const current = products.get(key);
            const totalSale = product.price * product.quantity;

            if (current) {
                current.total_sale += totalSale;
                current.total_qty += product.quantity;
                return;
            }

            const {
                title, variant_title, price, quantity, ...productData
            } = product;

            products.set(key, {
                ...productData,
                title: getTitle(title, variant_title),
                total_sale: totalSale,
                total_qty: quantity
            });
        });
    });

    if (products.size === 0)
        return { labels: ['Sin ventas'], data: [1] };

    const topProducts = [...products.values()]
        .sort((a, b) =>
            b.total_qty - a.total_qty
            || a.title.localeCompare(b.title, 'es', { sensitivity: 'base' })
            || String(a.prod_var_id).localeCompare(String(b.prod_var_id))
        )
        .slice(0, 10);

    return {
        labels: topProducts.map(product => product.title),
        data: topProducts.map(product => product.total_qty)
    };
};

export const getDashboardPeriodRange = (
    period, today, filteredRange = {}
) => {
    const monthStart = startOfMonth(today);
    const monthEnd = endOfMonth(today);
    const fortnight = getCurrentFortnight(today, monthStart, monthEnd);
    const ranges = {
        today: { startDate: startOfDay(today), endDate: endOfDay(today) },
        week: {
            startDate: startOfWeek(today, { weekStartsOn: 1 }),
            endDate: endOfWeek(today, { weekStartsOn: 1 })
        },
        fortnight: {
            startDate: fortnight.startRange,
            endDate: fortnight.endRange
        },
        month: { startDate: monthStart, endDate: monthEnd },
        quarter: {
            startDate: startOfQuarter(today),
            endDate: endOfQuarter(today)
        },
        yearToDate: { startDate: startOfYear(today), endDate: endOfDay(today) },
        annual: {
            startDate: startOfDay(subMonths(today, 12)),
            endDate: endOfDay(today)
        },
        filtered: {
            startDate: filteredRange.startDate || startOfYear(today),
            endDate: filteredRange.endDate || endOfDay(today)
        }
    };

    return ranges[period] || ranges.yearToDate;
};

export const getOrdersInDashboardRange = (
    ordersByYear, startDate, endDate
) => Object.values(ordersByYear)
    .flatMap(group => group.orders ?? [])
    .map(parseOrder)
    .filter(({ parsedDate }) => isWithinInterval(
        parsedDate, { start: startDate, end: endDate }
    ));

export const buildTopProductsRangeData = (
    ordersByYear, startDate, endDate
) => setData(orderTopProducts(
    getOrdersInDashboardRange(ordersByYear, startDate, endDate)
));

export const buildPeriodData = (filterStartRange, orders, today) => {
    const filteredRange = {
        startDate: filterStartRange,
        endDate: endOfDay(today)
    };
    const range = period => getDashboardPeriodRange(
        period, today, filteredRange
    );
    const inRange = (start, end) => orders.filter(({ parsedDate }) =>
        isWithinInterval(parsedDate, { start, end })
    );
    const ordersFor = period => {
        const { startDate, endDate } = range(period);
        return inRange(startDate, endDate);
    };
    const todayOrders = ordersFor('today');
    const weekOrders = ordersFor('week');
    const fortnightOrders = ordersFor('fortnight');
    const monthOrders = ordersFor('month');
    const quarterOrders = ordersFor('quarter');
    const yearToDateOrders = ordersFor('yearToDate');
    const annualOrders = ordersFor('annual');
    const filteredOrders = ordersFor('filtered');
    const todayRange = range('today');
    const weekRange = range('week');
    const fortnightRange = range('fortnight');
    const monthRange = range('month');
    const quarterRange = range('quarter');
    const yearToDateRange = range('yearToDate');
    const annualRange = range('annual');

    return {
        yearToDateOrders,
        dateRanges: {
            today: formatRange(todayRange.startDate, todayRange.endDate),
            week: formatRange(weekRange.startDate, weekRange.endDate),
            fortnight: formatRange(fortnightRange.startDate, fortnightRange.endDate),
            month: formatRange(monthRange.startDate, monthRange.endDate),
            quarter: formatRange(quarterRange.startDate, quarterRange.endDate),
            yearToDate: formatRange(yearToDateRange.startDate, yearToDateRange.endDate),
            annual: formatRange(annualRange.startDate, annualRange.endDate),
            filtered: formatRange(filterStartRange, today)
        },
        sales: {
            today: getTotal(todayOrders),
            week: getTotal(weekOrders),
            month: getTotal(monthOrders),
            quarter: getTotal(quarterOrders),
            yearToDate: getTotal(yearToDateOrders),
            filtered: getTotal(filteredOrders)
        },
        topProductsData: {
            todayData: setData(orderTopProducts(todayOrders)),
            weekData: setData(orderTopProducts(weekOrders)),
            fortnightData: setData(orderTopProducts(fortnightOrders)),
            monthData: setData(orderTopProducts(monthOrders)),
            quarterData: setData(orderTopProducts(quarterOrders)),
            yearToDateData: setData(orderTopProducts(yearToDateOrders)),
            annualData: setData(orderTopProducts(annualOrders)),
            filteredData: setData(orderTopProducts(filteredOrders))
        }
    };
};

const buildAverageTicketRows = (yearToDateOrders, today, info) => {
    const { years, customers } = info;
    const currentYear = today.getFullYear();
    const {
        generalTicketTable, generalTicketChart
    } = getAvgGeneralTicket(yearToDateOrders);
    const {
        clientTicketTable, clientTicketChart
    } = getAvgNewClientsTicket(customers, currentYear);

    return years.map(year => year === currentYear ? {
        year,
        generalTicketTable,
        generalTicketChart,
        clientTicketTable,
        clientTicketChart,
        isLoaded: true
    } : {
        year,
        generalTicketTable: [],
        generalTicketChart: [],
        clientTicketTable: [],
        clientTicketChart: [],
        isLoaded: false
    });
};

export const buildDashboardModel = (
    filterStartRange, orders, today, info
) => {
    const model = createDashboardInitialModel(today);
    const {
        years, customers, totalClients, semesterData,
        countrySales, topStates, monthClients,
        quarterClients, yearToDateClients
    } = info;
    const currentYear = today.getFullYear();

    model.customers = customers;

    if (orders.length === 0) {
        if (years.length > 0) {
            model.averageTicket = years.map(year => ({
                year,
                generalTicketTable: [],
                generalTicketChart: [],
                clientTicketTable: [],
                clientTicketChart: [],
                isLoaded: false
            }));
            model.year = years.includes(currentYear)
                ? currentYear
                : years.at(-1);
        } else {
            model.averageTicket = [{
                year: currentYear,
                generalTicketTable: [],
                generalTicketChart: [],
                clientTicketTable: [],
                clientTicketChart: [],
                isLoaded: true
            }];
            model.year = currentYear;
        }

        return model;
    }

    model.firstSemester = {
        ...model.firstSemester,
        datasets: model.firstSemester.datasets.map((dataset, index) =>
            index === 0
                ? { ...dataset, data: semesterData.slice(0, 6) }
                : dataset
        )
    };
    model.secondSemester = {
        ...model.secondSemester,
        datasets: model.secondSemester.datasets.map((dataset, index) =>
            index === 0
                ? { ...dataset, data: semesterData.slice(6, 12) }
                : dataset
        )
    };
    model.totalNewClients = totalClients;
    model.countrySalesData = { ...model.countrySalesData, ...countrySales };
    model.topStatesData = topStates;
    model.fqtMonthlyData = monthClients;
    model.fqtQuarterlyData = quarterClients;
    model.fqtYearToDateData = yearToDateClients;

    const parsedOrders = orders.map(parseOrder);
    const periodData = buildPeriodData(filterStartRange, parsedOrders, today);

    model.allOrders = {
        [currentYear]: { isLoaded: true, orders: parsedOrders }
    };
    model.averageTicket = buildAverageTicketRows(
        periodData.yearToDateOrders, today, info
    );
    model.year = currentYear;
    model.dateRanges = periodData.dateRanges;
    model.topProductsData = periodData.topProductsData;
    model.todayTotalData = periodData.sales.today;
    model.weekTotalData = periodData.sales.week;
    model.monthTotalData = periodData.sales.month;
    model.quarterTotalData = periodData.sales.quarter;
    model.yearToDateTotalData = periodData.sales.yearToDate;
    model.filteredTotalData = periodData.sales.filtered;

    return model;
};

export const getAverageTicketView = (ticketData) => {
    if (!ticketData?.isLoaded)
        return clone(AVERAGE_TICKET);

    return {
        generalTktTable: ticketData.generalTicketTable,
        clientTktTable: ticketData.clientTicketTable,
        generalTktGraph: {
            ...AVERAGE_TICKET.generalTktGraph,
            datasets: [{
                ...AVERAGE_TICKET.generalTktGraph.datasets[0],
                data: ticketData.generalTicketChart
            }]
        },
        clientTktGraph: {
            ...AVERAGE_TICKET.clientTktGraph,
            datasets: [{
                ...AVERAGE_TICKET.clientTktGraph.datasets[0],
                data: ticketData.clientTicketChart
            }]
        }
    };
};

export const mergeOrdersByYear = (
    groupedOrders, customers, currentOrders, averageTicket
) => {
    const parsedGrouped = {};
    const ticketByYear = new Map(
        averageTicket.map(ticket => [ticket.year, ticket])
    );

    Object.entries(groupedOrders).forEach(([yearKey, group]) => {
        const year = Number(yearKey);
        const parsedOrders = group.orders.map(parseOrder);
        const {
            generalTicketTable, generalTicketChart
        } = getAvgGeneralTicket(parsedOrders);
        const {
        clientTicketTable, clientTicketChart
        } = getAvgNewClientsTicket(customers, year);

        parsedGrouped[year] = { ...group, orders: parsedOrders };
        if (ticketByYear.has(year)) {
            ticketByYear.set(year, {
                year,
                isLoaded: true,
                generalTicketTable,
                generalTicketChart,
                clientTicketTable,
                clientTicketChart
            });
        }
    });

    return {
        allOrders: { ...currentOrders, ...parsedGrouped },
        averageTicket: [...ticketByYear.values()]
            .sort((a, b) => a.year - b.year)
    };
};

export const buildFilteredData = (
    ordersByYear, startDate, endDate, topProductsData, dateRanges
) => {
    const orders = Object.values(ordersByYear)
        .flatMap(group => group.orders ?? []);
    const filteredOrders = orders.filter(({ parsedDate }) =>
        isWithinInterval(parsedDate, { start: startDate, end: endDate })
    );
    return {
        dateRanges: {
            ...dateRanges,
            filtered: formatRange(startDate, endDate)
        },
        filteredTotalData: getTotal(filteredOrders),
        topProductsData: {
            ...topProductsData,
            filteredData: setData(orderTopProducts(filteredOrders))
        }
    };
};

export const getAvgGeneralTicket = (orders) => {
    const statsByMonth = {};

    orders.forEach((order) => {
        const month = getMonth(parseOrder(order).parsedDate);
        const current = statsByMonth[month] ?? { total: 0, count: 0 };

        current.total += order.summary.total;
        current.count += 1;
        statsByMonth[month] = current;
    });

    const chart = new Array(12).fill(0);
    const table = months.map((month, index) => {
        const data = statsByMonth[index];

        if (!data)
            return { mes: month, ganancia: '$0.00' };

        const average = Math.round((data.total / data.count) * 100) / 100;
        chart[index] = average;
        return { mes: month, ganancia: `$${average.toFixed(2)}` };
    });

    return { generalTicketTable: table, generalTicketChart: chart };
};

export const getAvgNewClientsTicket = (customers, selectedYear) => {
    const statsByMonth = {};

    customers.forEach((customer) => {
        if (customer.year !== selectedYear)
            return;

        const month = customer.month - 1;
        const current = statsByMonth[month] ?? { total: 0, count: 0 };
        current.total += getFirstPurchaseTotal(customer);
        current.count += 1;
        statsByMonth[month] = current;
    });

    const chart = new Array(12).fill(0);
    const table = months.map((month, index) => {
        const data = statsByMonth[index];

        if (!data)
            return { mes: month, ganancia: '$0.00' };

        const average = Math.round((data.total / data.count) * 100) / 100;
        chart[index] = average;
        return { mes: month, ganancia: `$${average.toFixed(2)}` };
    });

    return { clientTicketTable: table, clientTicketChart: chart };
};
